import type { Project } from '@/models/Project'

/**
 * The i18n key naming a project's kind — the short label the project lists use
 * as their heading ("QMS", "Products", "Regulatory watch").
 *
 * Two projects of the same company can share a name, so every picker that lets
 * someone hand out project access says which kind each one is. Returns `null`
 * rather than the raw enum value for a type we have no wording for: a server
 * -sent identifier must never reach the reader.
 */
export function projectTypeLabelKey(type: Project['type']): string | null {
    switch (type) {
        case 'qms':
            return 'projects.qms_projects'
        case 'td':
            return 'projects.products_projects'
        case 'regulatory_monitoring':
            return 'projects.regulatory_monitoring_projects'
        default:
            return null
    }
}

/**
 * The i18n key naming ONE project's kind, as a card or row tag reads it
 * ("QMS", "Produit", "Veille réglementaire") — singular, where
 * `projectTypeLabelKey` above is the plural list heading.
 *
 * It exists because `ProjectsGrid` printed `project.type` RAW
 * (`regulatory_monitoring`) on every card of the QMS, the Product AND the watch
 * index — a server-sent identifier on a customer's screen, which the frontend
 * rules forbid outright (R24). A type this build has no wording for falls back
 * to the generic noun, never to the value.
 */
export function projectTypeTagKey(
    type: Project['type'],
    /**
     * The index the card is on, when the type itself does not resolve. "Projet"
     * is not a word this product says to a customer (front-terminology skill: replace
     * "project" with the context's own term), and the shell always knows which
     * term that is — a card on the QMS index is a QMS whatever its stored type
     * says.
     */
    routeBase = '',
): string {
    switch (type) {
        case 'qms':
            return 'projects.type.qms'
        case 'td':
            return 'projects.type.td'
        case 'regulatory_monitoring':
            return 'projects.type.regulatory_monitoring'
        default:
            return shellTypeKey(routeBase)
    }
}

/** The kind an index is FOR — `projectRouteBase`'s three values, read back. */
function shellTypeKey(routeBase: string): string {
    if (routeBase.startsWith('/qms')) return 'projects.type.qms'
    if (routeBase.startsWith('/regulatory-monitoring')) return 'projects.type.regulatory_monitoring'
    return 'projects.type.td'
}
